import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CalendarGrid } from '../components/CalendarGrid';
import { EventsGridView } from '../components/EventsGridView';
import { EventsListView } from '../components/EventsListView';
import { Calendar as CalendarIcon, Grid, List, Disc3, Tv, Star, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const featuredEvents = [{
  icon: Disc3,
  title: 'Live DJ Nights',
  description: 'Every Friday & Saturday featuring top DJs spinning the hottest tracks while you enjoy premium bottle service.',
  detail: 'Fridays & Saturdays • 11PM-3AM'
}, {
  icon: Tv,
  title: 'Sports Viewing Parties',
  description: 'Watch the biggest games on our massive 190ft screen with surround sound and VIP seating options.',
  detail: 'Game Days • Check Schedule'
}, {
  icon: Star,
  title: 'Celebrity Appearances',
  description: 'Meet movie stars, athletes, and entertainers in an exclusive intimate setting with photo opportunities.',
  detail: 'Special Events • By Invitation'
}];
export function EventsCalendar() {
  const [view, setView] = useState<'grid' | 'calendar' | 'list'>('calendar');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState('2026-01-23');
  const [endDate, setEndDate] = useState('2026-03-23');
  const datePickerRef = useRef<HTMLDivElement>(null);
  // Close date picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).toUpperCase();
  };
  return <div className="min-h-screen bg-[#0B1014] text-white selection:bg-[#DDA247] selection:text-[#0B1014]" data-id="element-984">
      <Header data-id="element-985" />

      <main className="pt-32 pb-24 px-4 md:px-8 max-w-[1600px] mx-auto" data-id="element-986">
        {/* Upcoming Events Hero */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-987">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-wide" data-id="element-988">
            UPCOMING EVENTS
          </h1>
          <div className="w-24 h-1 bg-[#DDA247] mx-auto mb-6" data-id="element-989" />
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-990">
            Experience world-class entertainment on our 190ft immersive video
            wall. From live DJ nights to celebrity appearances, sports viewing
            parties to comedy shows.
          </p>
        </motion.div>

        {/* Featured Event Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-991">
          {featuredEvents.map((event, index) => <motion.div key={event.title} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.15,
          duration: 0.6
        }} className="bg-[#11161A] border border-white/5 p-8 text-center" data-id="element-992">
              <div className="inline-flex items-center justify-center w-14 h-14 mb-5 bg-[#DDA247]/10 rounded-full" data-id="element-993">
                <event.icon className="w-7 h-7 text-[#DDA247]" data-id="element-994" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 tracking-wide" data-id="element-995">
                {event.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-996">
                {event.description}
              </p>
              <p className="text-[#DDA247] text-xs font-medium tracking-widest uppercase" data-id="element-997">
                {event.detail}
              </p>
            </motion.div>)}
        </div>

        {/* Controls Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6" data-id="element-998">
          {/* Date Range Picker */}
          <div className="relative" ref={datePickerRef} data-id="element-999">
            <button onClick={() => setIsDatePickerOpen(!isDatePickerOpen)} className="flex items-center space-x-3 bg-white text-[#0B1014] px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#DDA247] transition-colors duration-300" data-id="element-1000">
              <span data-id="element-1001">
                {formatDate(startDate)} - {formatDate(endDate)}
              </span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isDatePickerOpen ? 'rotate-180' : ''}`} data-id="element-1002" />
            </button>

            <AnimatePresence data-id="element-1003">
              {isDatePickerOpen && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.2
            }} className="absolute top-full left-0 mt-2 bg-[#11161A] border border-[#DDA247]/20 shadow-2xl p-6 z-50 min-w-[300px]" data-id="element-1004">
                  <div className="space-y-4" data-id="element-1005">
                    <div data-id="element-1006">
                      <label className="block text-xs text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-1007">
                        Start Date
                      </label>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full bg-[#0B1014] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#DDA247] transition-colors [color-scheme:dark]" data-id="element-1008" />
                    </div>
                    <div data-id="element-1009">
                      <label className="block text-xs text-gray-400 font-medium tracking-widest uppercase mb-2" data-id="element-1010">
                        End Date
                      </label>
                      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full bg-[#0B1014] border border-white/10 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#DDA247] transition-colors [color-scheme:dark]" data-id="element-1011" />
                    </div>
                    <button onClick={() => setIsDatePickerOpen(false)} className="w-full bg-[#DDA247] text-[#0B1014] py-3 font-bold text-sm uppercase tracking-wider hover:bg-white transition-colors duration-300" data-id="element-1012">
                      Apply
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* View Toggles */}
          <div className="flex items-center space-x-2 bg-white/5 p-1 rounded" data-id="element-1013">
            <button onClick={() => setView('grid')} className={`p-3 rounded transition-all duration-300 ${view === 'grid' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="Grid View" data-id="element-1014">
              <Grid className="w-5 h-5" data-id="element-1015" />
            </button>
            <button onClick={() => setView('calendar')} className={`p-3 rounded transition-all duration-300 ${view === 'calendar' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="Calendar View" data-id="element-1016">
              <CalendarIcon className="w-5 h-5" data-id="element-1017" />
            </button>
            <button onClick={() => setView('list')} className={`p-3 rounded transition-all duration-300 ${view === 'list' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="List View" data-id="element-1018">
              <List className="w-5 h-5" data-id="element-1019" />
            </button>
          </div>
        </div>

        {/* Calendar Content with Transitions */}
        <AnimatePresence mode="wait" data-id="element-1020">
          {view === 'calendar' && <motion.div key="calendar" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1021">
              <CalendarGrid data-id="element-1022" />
            </motion.div>}

          {view === 'grid' && <motion.div key="grid" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1023">
              <EventsGridView data-id="element-1024" />
            </motion.div>}

          {view === 'list' && <motion.div key="list" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1025">
              <EventsListView data-id="element-1026" />
            </motion.div>}
        </AnimatePresence>
      </main>

      <Footer data-id="element-1027" />
    </div>;
}